<template>
  <div ref="root" class="qp-notifications">
    <button
      type="button"
      class="qp-notifications__button"
      :class="{ 'qp-notifications__button--active': isOpen }"
      :aria-label="$t('notifications.title')"
      :title="$t('notifications.title')"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      @click="toggleMenu"
    >
      <font-awesome-icon :icon="'bell'"/>
      <span v-if="unreadCount > 0" class="qp-notifications__badge">
        {{ unreadBadge }}
      </span>
    </button>

    <div v-show="isOpen" class="qp-notifications__menu" role="menu">
      <header class="qp-notifications__header">
        <div>
          <span class="qp-notifications__title">{{ $t('notifications.title') }}</span>
          <span class="qp-notifications__meta">
            {{ $t('notifications.unread_count', { count: unreadCount }) }}
          </span>
        </div>
        <button
          v-if="unreadCount > 0"
          type="button"
          class="qp-notifications__mark-all"
          :disabled="isMarkingAll"
          @click="markAllRead"
        >
          <font-awesome-icon :icon="isMarkingAll ? 'spinner' : 'check-double'" :spin="isMarkingAll" />
          <span>{{ $t('notifications.mark_all_read') }}</span>
        </button>
      </header>

      <div class="qp-notifications__list">
        <div v-if="isLoading && notifications.length === 0" class="qp-notifications__state">
          {{ $t('common.loading') }}
        </div>

        <button
          v-for="notification in notifications"
          :key="notification.id"
          type="button"
          class="qp-notifications__item"
          :class="{ 'qp-notifications__item--unread': !notification.is_read }"
          role="menuitem"
          @click="openNotification(notification)"
        >
          <span class="qp-notifications__dot" aria-hidden="true"></span>
          <span class="qp-notifications__content">
            <span class="qp-notifications__item-title">{{ getNotificationTitle(notification) }}</span>
            <span v-if="getNotificationMessage(notification)" class="qp-notifications__message">
              {{ getNotificationMessage(notification) }}
            </span>
            <span class="qp-notifications__time">{{ formatDateTime(notification.created_at) }}</span>
          </span>
        </button>

        <div v-if="!isLoading && notifications.length === 0" class="qp-notifications__state">
          {{ $t('notifications.empty') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { authState } from '@/store/auth';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/services/notifications';

const POLL_INTERVAL_MS = 45000;

export default {
  name: 'NotificationsMenu',
  setup() {
    const router = useRouter();
    const { locale, t } = useI18n();
    const root = ref(null);
    const isOpen = ref(false);
    const isLoading = ref(false);
    const isMarkingAll = ref(false);
    const notifications = ref([]);
    const unreadCount = ref(0);
    let pollTimer = null;
    let soundContext = null;
    let soundUnlocked = false;

    const unreadBadge = computed(() => (unreadCount.value > 99 ? '99+' : String(unreadCount.value)));

    const refreshNotifications = async ({ silent = false } = {}) => {
      if (!authState.isAuthenticated) return;
      if (!silent) isLoading.value = true;
      try {
        const data = await fetchNotifications({ limit: 10, offset: 0 });
        notifications.value = Array.isArray(data.notifications) ? data.notifications : [];
        unreadCount.value = Number(data.unread_count || 0);
      } catch (_) {
        if (!silent) notifications.value = [];
      } finally {
        if (!silent) isLoading.value = false;
      }
    };

    const loadNotifications = () => refreshNotifications({ silent: false });

    // Фоновый поллинг дергает только лёгкий /unread_count; полный список
    // грузится при открытии меню или когда счётчик изменился на виду.
    const refreshUnreadCount = async ({ notify = false } = {}) => {
      if (!authState.isAuthenticated) return;
      try {
        const data = await fetchUnreadCount();
        const nextCount = Number(data.unread_count || 0);
        if (notify && nextCount > unreadCount.value && soundUnlocked) {
          playNotificationSound();
        }
        if (nextCount !== unreadCount.value && isOpen.value) {
          refreshNotifications({ silent: true });
        }
        unreadCount.value = nextCount;
      } catch (_) {
        // Сетевые сбои поллинга игнорируем — следующий тик повторит запрос.
      }
    };

    const startPolling = () => {
      stopPolling();
      if (!authState.isAuthenticated || document.hidden) return;
      pollTimer = window.setInterval(() => {
        refreshUnreadCount({ notify: true });
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    // В фоновых вкладках поллинг останавливается, при возврате — сразу
    // один запрос и обычный интервал.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (authState.isAuthenticated) {
        refreshUnreadCount({ notify: true });
        startPolling();
      }
    };

    const getSoundContext = () => {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return null;
      if (!soundContext) {
        soundContext = new AudioContext();
      }
      return soundContext;
    };

    const unlockNotificationSound = async () => {
      const context = getSoundContext();
      if (!context) return;
      try {
        if (context.state === 'suspended') {
          await context.resume();
        }
        soundUnlocked = true;
      } catch (_) {
        soundUnlocked = false;
      }
    };

    const playNotificationSound = async () => {
      const context = getSoundContext();
      if (!context) return;
      try {
        if (context.state === 'suspended') {
          await context.resume();
        }
        const now = context.currentTime;
        const gain = context.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
        gain.connect(context.destination);

        [740, 980].forEach((frequency, index) => {
          const oscillator = context.createOscillator();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(frequency, now + index * 0.12);
          oscillator.connect(gain);
          oscillator.start(now + index * 0.12);
          oscillator.stop(now + index * 0.12 + 0.22);
        });
      } catch (_) {
        // Browsers may block sound until the first user gesture.
      }
    };

    const getI18nPayload = (notification) => {
      const i18n = notification?.payload?.i18n;
      return i18n && typeof i18n === 'object' ? i18n : {};
    };

    const translateNotification = (notification, field) => {
      const i18n = getI18nPayload(notification);
      const key = i18n[`${field}_key`];
      const params = i18n.params || {};
      if (!key) return notification?.[field] || '';
      const translated = t(key, params);
      return translated && translated !== key ? translated : (notification?.[field] || '');
    };

    const getNotificationTitle = (notification) => translateNotification(notification, 'title');
    const getNotificationMessage = (notification) => translateNotification(notification, 'message');

    const toggleMenu = async () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        await loadNotifications();
      }
    };

    const markLocalRead = (notificationId) => {
      notifications.value = notifications.value.map((item) => (
        item.id === notificationId ? { ...item, is_read: true, read_at: new Date().toISOString() } : item
      ));
      unreadCount.value = Math.max(unreadCount.value - 1, 0);
    };

    const openNotification = async (notification) => {
      if (!notification) return;
      if (!notification.is_read) {
        try {
          await markNotificationRead(notification.id);
          markLocalRead(notification.id);
        } catch (error) {
          // Navigation should still work if marking failed.
        }
      }

      isOpen.value = false;
      const link = notification.link_url;
      if (!link) return;
      if (/^https?:\/\//i.test(link)) {
        window.location.assign(link);
        return;
      }
      router.push(link).catch(() => {});
    };

    const markAllRead = async () => {
      if (unreadCount.value <= 0) return;
      isMarkingAll.value = true;
      try {
        await markAllNotificationsRead();
        notifications.value = notifications.value.map((item) => ({
          ...item,
          is_read: true,
          read_at: item.read_at || new Date().toISOString(),
        }));
        unreadCount.value = 0;
      } finally {
        isMarkingAll.value = false;
      }
    };

    const handleClickOutside = (event) => {
      if (isOpen.value && root.value && !root.value.contains(event.target)) {
        isOpen.value = false;
      }
    };

    const formatDateTime = (value) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const localeMap = { ru: 'ru-RU', en: 'en-US', kk: 'kk-KZ', zh: 'zh-CN' };
      return new Intl.DateTimeFormat(localeMap[locale.value] || 'ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    onMounted(async () => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('pointerdown', unlockNotificationSound, { once: true });
      document.addEventListener('keydown', unlockNotificationSound, { once: true });
      if (authState.isAuthenticated) {
        await refreshUnreadCount();
        startPolling();
      }
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('pointerdown', unlockNotificationSound);
      document.removeEventListener('keydown', unlockNotificationSound);
      stopPolling();
      if (soundContext) {
        soundContext.close().catch(() => {});
        soundContext = null;
      }
    });

    watch(
      () => authState.isAuthenticated,
      async (isAuthenticated) => {
        if (isAuthenticated) {
          await refreshUnreadCount();
          startPolling();
        } else {
          isOpen.value = false;
          notifications.value = [];
          unreadCount.value = 0;
          stopPolling();
        }
      }
    );

    return {
      root,
      isOpen,
      isLoading,
      isMarkingAll,
      notifications,
      unreadCount,
      unreadBadge,
      toggleMenu,
      loadNotifications,
      openNotification,
      markAllRead,
      formatDateTime,
      getNotificationTitle,
      getNotificationMessage,
    };
  },
};
</script>

<style scoped>
.qp-notifications {
  position: relative;
}

.qp-notifications__button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--ink-muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition:
    color var(--dur-xs) var(--ease-out-quart),
    background-color var(--dur-xs) var(--ease-out-quart),
    border-color var(--dur-xs) var(--ease-out-quart);
}

.qp-notifications__button:hover,
.qp-notifications__button--active {
  color: var(--ink);
  background: var(--surface-sunken);
  border-color: var(--hairline);
}

.qp-notifications__badge {
  position: absolute;
  inset: -4px -5px auto auto;
  min-width: 17px;
  height: 17px;
  padding-inline: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: var(--weight-semibold);
  line-height: 1;
  color: var(--accent-contrast);
  background: var(--danger);
  border: 2px solid var(--surface-base);
  border-radius: var(--radius-pill);
}

.qp-notifications__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(360px, calc(100vw - 24px));
  overflow: hidden;
  background: var(--surface-base);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: var(--z-dropdown);
  animation: qp-notifications-in 220ms var(--ease-out-expo);
}

@keyframes qp-notifications-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.qp-notifications__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--hairline);
}

.qp-notifications__title {
  display: block;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
}

.qp-notifications__meta {
  display: block;
  margin-block-start: 2px;
  font-size: var(--text-2xs);
  color: var(--ink-faint);
}

.qp-notifications__mark-all,
.qp-notifications__refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 8px;
  font-family: inherit;
  font-size: var(--text-2xs);
  font-weight: var(--weight-medium);
  color: var(--accent);
  background: var(--accent-tint);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.qp-notifications__mark-all:disabled,
.qp-notifications__refresh:disabled {
  cursor: default;
  opacity: 0.55;
}

.qp-notifications__list {
  max-height: min(420px, calc(100vh - 180px));
  overflow-y: auto;
}

.qp-notifications__item {
  display: grid;
  grid-template-columns: 8px 1fr;
  gap: var(--space-sm);
  width: 100%;
  padding: 10px var(--space-md);
  font-family: inherit;
  text-align: start;
  color: var(--ink);
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--hairline);
  cursor: pointer;
}

.qp-notifications__item:hover {
  background: var(--surface-sunken);
}

.qp-notifications__item--unread {
  background: var(--accent-tint);
}

.qp-notifications__dot {
  width: 7px;
  height: 7px;
  margin-block-start: 7px;
  border-radius: var(--radius-pill);
  background: transparent;
}

.qp-notifications__item--unread .qp-notifications__dot {
  background: var(--accent);
}

.qp-notifications__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.qp-notifications__item-title {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
}

.qp-notifications__message {
  font-size: var(--text-xs);
  line-height: 1.35;
  color: var(--ink-muted);
  overflow-wrap: anywhere;
}

.qp-notifications__time {
  font-size: var(--text-2xs);
  color: var(--ink-faint);
}

.qp-notifications__state {
  padding: var(--space-lg) var(--space-md);
  font-size: var(--text-sm);
  color: var(--ink-muted);
  text-align: center;
}

.qp-notifications__footer {
  display: flex;
  justify-content: flex-end;
  padding: 6px var(--space-md);
  border-top: 1px solid var(--hairline);
}

@media (max-width: 560px) {
  .qp-notifications__menu {
    position: fixed;
    top: calc(var(--layout-navbar-h-mobile) + 6px);
    right: var(--space-sm);
    left: var(--space-sm);
    width: auto;
  }

  .qp-notifications__mark-all span,
  .qp-notifications__refresh span {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .qp-notifications__button,
  .qp-notifications__menu {
    animation: none;
    transition: none;
  }
}
</style>
